import React, {
    useEffect, useContext, useState, useCallback
} from 'react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { TranslatorContext, IntlContext } from '@jutro/locale';
import _ from 'lodash';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { withRouter, Link } from 'react-router-dom';
import { ClaimService } from 'gw-capability-claim';
import { useAuthentication } from 'gw-digital-auth-react';
import { Icon, DateField } from '@jutro/components';
import { LobIconUtil } from 'gw-portals-util-js';
import { getNormalizedLOBName } from 'gw-portals-config-js';
import ClaimLobOptionsMenu from '../ClaimLobOptionsMenu/ClaimLobOptionsMenu';
import VendorLinkComponent from '../VendorLinkComponent/VendorLinkComponent';
import metadata from './ClaimTableComponent.metadata.json5';
import styles from '../../pages/ClaimList/ClaimList.module.scss';
import claimsMessages from '../../Claims.messages';
import { ClickableCard } from '@jutro/components';
import { ButtonLink } from '@jutro/router';
import { getFontIcon } from "../../../../module/nexus-portals-util-js/lobIconUtil";

function ClaimTableComponent({ history }) {
    const { authHeader } = useAuthentication();
    const { vendorTypesForPolicyHolderByLob, claimsFilterLOBOptions } = appConfig;
    const [claimDataTable, setClaimDataTable] = useState('');
    const [isLoading, setLoadingState] = useState(false);

    const [selectedClaimLOB, setSelectedClaimLOBe] = useState(undefined);
    const [searchFilter, setSearchFilter] = useState(undefined);
    const [isClosed, setIsClosed] = useState(false);
    const translator = useContext(TranslatorContext);
    const intl = useContext(IntlContext);
    const [myCards] = [{ "LOB": "PP" }];

    const getVendors = useCallback(
        (claim) => {
            if (!claim.vendors) {
                return [];
            }
            const normalizedLobCode = getNormalizedLOBName(claim.lineOfBusinessCode);
            const usedVendorTypes = vendorTypesForPolicyHolderByLob[normalizedLobCode] || [];

            const policyHolderVendors = claim.vendors.filter((vendor) => {
                let type = vendor.contactDTO.subtype;
                if (normalizedLobCode === 'ho') {
                    type = vendor.contactDTO.contactType;
                }
                return usedVendorTypes.indexOf(type) >= 0;
            });
            return policyHolderVendors;
        },
        [vendorTypesForPolicyHolderByLob]
    );

    const processClaimsData = useCallback(
        (claimsData) => {
            if (!_.isEmpty(claimsData)) {
                let claimsArrayResult = claimsData;
                claimsArrayResult = claimsArrayResult.map((claimInfo) => {
                    const claims = {
                        product: claimInfo.lineOfBusinessCode,
                        claimNumber: claimInfo.claimNumber,
                        dateOfLoss: intl.formatDate(new Date(claimInfo.lossDate), { year: 'numeric', month: 'short', day: 'numeric' }),
                        status: translator({
                            id: `typekey.ClaimState.${claimInfo.claimState}`,
                            defaultMessage: claimInfo.claimState
                        }),
                        policyNumber: claimInfo.policyNumber,
                        vendors: getVendors(claimInfo)
                    };
                    return claims;
                });
                return claimsArrayResult;
            }
            return [];
        },
        [getVendors, intl, translator]
    );

    const getClaims = useCallback(
        (query) => {
            const claimsPromise = ClaimService.getClaimSummaries(query, {}, authHeader);
            return claimsPromise
                .then((responseData) => {
                    const data = processClaimsData(responseData.items);
                    setClaimDataTable(data);
                })
                .finally(() => {
                    setLoadingState(false);
                });
        },
        [authHeader, processClaimsData]
    );

    const loadClaimData = useCallback(
        (filters) => {
            setLoadingState(true);

            let claimStates = isClosed ? ['open', 'draft', 'closed'] : ['open', 'draft'];
            let policyType = selectedClaimLOB !== translator(claimsMessages.allClaims)
                ? selectedClaimLOB
                : undefined;
            let queryText = searchFilter;

            if (!_.isUndefined(filters)) {
                if (!_.isUndefined(filters.isClosed)) {
                    claimStates = filters.isClosed
                        ? ['open', 'draft', 'closed']
                        : ['open', 'draft'];
                }
                if (!_.isUndefined(filters.policyType)) {
                    policyType = filters.policyType !== translator(claimsMessages.allClaims)
                        ? filters.policyType
                        : undefined;
                }
                queryText = !_.isUndefined(filters.queryText) ? filters.queryText : queryText;
            }

            const query = {
                claimStates: claimStates,
                policyType: policyType,
                queryText: queryText
            };

            return getClaims(query);
        },
        [selectedClaimLOB, searchFilter, isClosed, getClaims, translator]
    );

    useEffect(() => {
        loadClaimData();
        setSelectedClaimLOBe(translator(claimsMessages.allClaims));

        // Disabling to prevent continues re-rendering
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getCellProduct = (rowData) => {
        const icon = getFontIcon(rowData.product);
        return <span className="iconFocus" tabindex="0"><Icon icon={icon} title={translator({ id: `typekey.LOBCode.${rowData.product}`})} key={rowData.product} /></span>;
    
    };

    const getCellClaimPolicyNumber = (rowData) => {
        return (
            <span>
                {rowData.policyNumber}
            </span>
        );
    };

    const getCellDateOfLoss = (rowData, rowIndex) => {
        return (
            <DateField
                id={`dateOfLoss_${rowIndex}`}
                value={rowData.dateOfLoss}
                format="short"
                dataType={_.isString(rowData.dateOfLoss) ? 'string' : 'object'}
                readOnly
                hideLabel
                layout="full-width"
                showOptional={false}
            />
        );
    };

    const getCellClaimNumber = (rowData) => {
        return (
            <span>
                {rowData.claimNumber}
            </span>
        );
    };

    const getCellClaimDetail = (rowData, rowIndex) => {
        if (rowData.status === 'Draft') {
            const redirectPath = {
                pathname: '/fnol-select-policy',
                state: {
                    claimNumber: rowData.claimNumber,
                    redirectPath: '/claims',
                    claimStatus: rowData.status
                }
            };
            return (
                <ButtonLink
                    id={`viewPolicy_${rowIndex}`}
                    className={styles.link}
                    to={redirectPath}
                >
                    Details
                </ButtonLink>
            );
        } else {
            const redirectPath = {
                pathname: `/claims/${rowData.claimNumber}`,
                state: {
                    claimNumber: rowData.claimNumber,
                    redirectPath: '/claims',
                    claimStatus: rowData.status
                }
            };
            return (
                <ButtonLink
                    id={`viewPolicy_${rowIndex}`}
                    className={styles.link}
                    to={redirectPath}
                >
                    Details
                </ButtonLink>
            );
        }
    };

    const getCellClaimStatus = (rowData, rowIndex, rowProperty) => {
        const titleMessage = translator(claimsMessages.claimStatus);
        const status = rowData[rowProperty.id];
        return (
            <span>
                {rowData[rowProperty.id]}
            </span>
        );
    };

    /* 
    const handleFilterChange = useCallback(
        (value, id) => {
            if (id === 'LOBFilter' && value !== 'Filters') {
                setSelectedClaimLOBe(value);
                loadClaimData({ policyType: value });
            }
            if (id === 'searchFilter') {
                loadClaimData({ queryText: value });
            }
            if (id === 'closedFilter') {
                setIsClosed(value);
                loadClaimData({ isClosed: value });
            }
        },
        [loadClaimData]
    );

    const getCell = useCallback(
        (item, index, property) => {
            const toolTipMessage = {
                product: translator(claimsMessages.line),
                dateOfLoss: translator(claimsMessages.dateOfLossClaimDetail)
            };
            return <span title={toolTipMessage[property.id]}>{item[property.id]}</span>;
        },
        [translator]
    );

    const getStatusCell = useCallback(
        (item, index, property) => {
            const titleMessage = translator(claimsMessages.claimStatus);
            const status = item[property.id];
            let typeStatus = 'success';

            if (status === translator({ id: 'typekey.ClaimState.draft', defaultMessage: 'draft' })) {
                typeStatus = 'warning';
            } else if (status === translator({ id: 'typekey.ClaimState.closed', defaultMessage: 'closed' })) {
                typeStatus = 'neutral';
            }

            return (
                <InfoLabel type={typeStatus} size="small" title={titleMessage[property.id]}>
                    {item[property.id]}
                </InfoLabel>
            );
        },
        [translator]
    );

    const getVendorsCell = useCallback((item, index, property) => {
        const vendors = item[property.id];
        return <VendorLinkComponent id="vendorsCell" vendors={vendors} />;
    }, []);

    const getLink = useCallback(
        (item, index, property) => {
            const toolTipMessage = {
                policyNumber: translator(claimsMessages.policyNumberClaimDetail),
                claimNumber: translator(claimsMessages.claimNumber)
            };
            if (
                property.id === 'claimNumber'
                && item.status
                    === translator({ id: 'typekey.ClaimState.draft', defaultMessage: 'draft' })
            ) {
                const redirectpath = {
                    pathname: '/fnol-select-policy',
                    state: {
                        claimNumber: item[property.id],
                        redirectPath: '/claims',
                        claimStatus: item.status
                    }
                };
                return (
                    <Link
                        to={redirectpath}
                        className={styles.gwClaimsLinkContent}
                        title={toolTipMessage[property.id]}
                    >
                        {item[property.id]}
                    </Link>
                );
            }
            const redirectRoute = property.id === 'policyNumber'
                ? `/claims/policy/${item.claimNumber}`
                : `/claims/${item[property.id]}`;
            const redirectPath = {
                pathname: redirectRoute,
                state: {
                    claimNumber: item[property.id],
                    redirectPath: '/claims',
                    claimStatus: item.status
                }
            };
            return (
                <Link
                    to={redirectPath}
                    className={styles.gwClaimsLinkContent}
                    title={toolTipMessage[property.id]}
                >
                    {item[property.id]}
                </Link>
            );
        },
        [translator]
    );

    const getProductIcon = useCallback((item) => {
       //const icon = LobIconUtil.getMaterialIcon(item.product);
       const icon = getFontIcon(item.product);

        return <Icon icon={icon} title={item.product} key={item.product} />;
    }, []);

    const handleFileClick = useCallback(() => {
        return history.push({
            pathname: '/fnol-select-policy',
            state: {
                redirectPath: '/claims'
            }
        });
    }, [history]);

    //Claim icon
    const getClaimPolicyIcon = useCallback(() => {
       const overrides = claimDataTable.map((claim, index) => {
            return {
                [`lob${index}`]: {
                    content: getProductIcon(claim)
                }
            };
        });

        return Object.assign({}, ...overrides);
    }, [claimDataTable]);

    const getButtonLink = useCallback((item, index, property) => {
            const overrides = claimDataTable.map((claim, index) => {
                return {
                    [`claimCards${index}`]: {
                        content: getClaimLink(claim)
                    }
                };
            }); 

            return <></>;
        }, []); */


    const generateClaimDetailsOverrides = useCallback(() => {
        return {
            productColumn: { onCell: getCellProduct },
            policyNumberClaimColumn: { onCell: getCellClaimPolicyNumber },
            claimNumberColumn: { onCell: getCellClaimNumber },
            dateOfLossColumn: { onCell: getCellDateOfLoss },
            statusColumn: { onCell: getCellClaimStatus },
            claimDetailViewColumn: {
                onCell: getCellClaimDetail
            }
        };
    }, [claimDataTable]);

    /*  const getClaimLink = (rowData, rowIndex) => {
      *//*
       const activePeriod = _.first(rowData.periods);
       const periodWithIDCard = rowData.periods.find((policy) => policy.idCardPublicID); *//*
    return (
       <>
            <br />

        </>
    );
}; 
const getClaimLink = (e) => {
        return true;
    }; */



    const override = {
        claimListTableLoader: {
            loaded: !isLoading
        },
        claimTable: {
            data: claimDataTable,
            visible: !isLoading
        },

        noDataMessageContainer: {
            visible: _.isEmpty(claimDataTable) && !isLoading
        },
        noDataMessage: {
            value: translator(claimsMessages.noClaimsResult)
        },
        ...generateClaimDetailsOverrides()

    };
    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            //getCell: getCell,
            //getLink: getLink,
            //getProductIcon: getProductIcon,
            //getVendorsCell: getVendorsCell,
            //getStatusCell: getStatusCell,
            //handleFilterValue: handleFilterChange,
            //handleClosedClaims: setIsClosed,
            //handleFileClick: handleFileClick,
            //getClaimLink: getClaimLink,
            //getButtonLink : getButtonLink
        },
        resolveComponentMap: {
            claimloboptionsmenu: ClaimLobOptionsMenu
        }
    };

    return renderContentFromMetadata(metadata.componentContent, override, resolvers);
}

export default withRouter(ClaimTableComponent);
